import React from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Mic, HeadphonesIcon, MessageSquare, Settings } from 'lucide-react'

const GettingStartedPage = () => {
  const steps = [
    {
      title: 'Set Up Your Profile',
      description: 'Tell us about your English level and learning goals',
      icon: Settings,
      completed: true
    },
    {
      title: 'Choose Your AI Teacher',
      description: 'Select from various AI personalities and teaching styles',
      icon: MessageSquare,
      completed: true
    },
    {
      title: 'Test Your Equipment',
      description: 'Make sure your microphone and speakers are working properly',
      icon: HeadphonesIcon,
      completed: false
    },
    {
      title: 'Start Your First Conversation',
      description: 'Have a simple introductory chat with your AI teacher',
      icon: Mic,
      completed: false
    }
  ]

  const quickTips = [
    {
      title: 'Speak Naturally',
      description: 'Don\'t worry about perfect pronunciation. The AI will help you improve gradually.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Practice Daily',
      description: 'Even 10 minutes a day will help you build confidence and fluency.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Ask Questions',
      description: 'Your AI teacher is patient and can explain grammar, vocabulary, and pronunciation.',
      color: 'from-green-500 to-teal-500'
    },
    {
      title: 'Use Real Scenarios',
      description: 'Practice conversations you might have in real life for practical learning.',
      color: 'from-orange-500 to-red-500'
    }
  ]

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Getting Started with <span className="gradient-text">AI English Learning</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Welcome to VivaLingua! Follow these simple steps to begin your English speaking journey with AI.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-white mb-8">Your Setup Progress</h2>
          <div className="space-y-4">
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <div
                  key={index}
                  className={`flex items-center p-6 rounded-xl border transition-all duration-300 ${
                    step.completed
                      ? 'bg-green-500/10 border-green-500/30 text-green-400'
                      : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <div className="flex-shrink-0 mr-4">
                    {step.completed ? (
                      <CheckCircle className="h-8 w-8 text-green-400" />
                    ) : (
                      <div className="w-8 h-8 rounded-full border-2 border-slate-600 flex items-center justify-center">
                        <span className="text-sm font-medium">{index + 1}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 mr-4">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1">{step.title}</h3>
                    <p className="text-sm opacity-80">{step.description}</p>
                  </div>
                  {!step.completed && (
                    <ArrowRight className="h-5 w-5 text-blue-400" />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Quick Tips */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-white mb-8">Tips for Success</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickTips.map((tip, index) => (
              <div
                key={index}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300"
              >
                <div className={`inline-block w-12 h-12 bg-gradient-to-br ${tip.color} rounded-lg mb-4 flex items-center justify-center`}>
                  <span className="text-white font-bold text-lg">{index + 1}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{tip.title}</h3>
                <p className="text-slate-300">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-8 border border-blue-500/30">
          <h2 className="text-2xl font-semibold text-white mb-4">Ready to Start?</h2>
          <p className="text-slate-300 mb-6">
            You're all set! Now it's time to dive into our comprehensive guides and start practicing 
            your English speaking skills with AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://vivalingua.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 group"
            >
              Start First Conversation
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/guides"
              className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Browse All Guides
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GettingStartedPage